import { useMemo } from 'react'

import { isDev, isTestServer } from '@twistopayments/design-system/environment'
import { atom, useAtom } from 'jotai'
import { useRouter } from 'next/router'
import { isEmpty, isNil } from 'ramda'

import { envConfig } from '../env-config'
import { regionObjects } from '../region'
import { remoteLanguageAtom } from '../remote-language'

type Config = {
  region?: GLOBALS.Region
  language?: GLOBALS.Language
}

type ConfigEditorOverride = Config & {
  featureFlags?: Record<string, boolean | undefined>
}

export const configVersionAtom = atom(1)

export const setupAppConfig = (
  config?: Config,
  configEditorOverride?: ConfigEditorOverride
) => {
  const baseConfig = {
    region: envConfig.REGION_LOCKED
      ? envConfig.region
      : config?.region ?? envConfig.region,
  }
  const isBrowser = typeof window !== 'undefined'

  // resolve region on localhost (not applied for `cz.twisto.top`)
  if (
    !envConfig.REGION_LOCKED &&
    isDev &&
    isBrowser &&
    /localhost/.test(window.location.host)
  ) {
    baseConfig.region =
      configEditorOverride?.region ?? config?.region ?? baseConfig.region
  }

  const extendedConfig = {
    ...regionObjects[baseConfig.region],
    language:
      config?.language ??
      regionObjects[baseConfig.region].defaultRegionLanguage,
  }

  // resolve featureFlags
  if (isDev || isTestServer) {
    extendedConfig.featureFlags = {
      ...extendedConfig.featureFlags,
      ...configEditorOverride?.featureFlags,
    }
  }

  // resolve language in dev
  if (isDev) {
    const storedLanguage = !isEmpty(configEditorOverride?.language)
      ? configEditorOverride?.language
      : undefined

    extendedConfig.language =
      storedLanguage ?? config?.language ?? extendedConfig.defaultRegionLanguage
  }

  const finalConfig = Object.freeze({
    ...extendedConfig,
    ...baseConfig,
  })

  if (isBrowser) {
    window.LANG = finalConfig.language
    window.REGION = finalConfig.region
  }

  return finalConfig
}

export type AppConfig = ReturnType<typeof setupAppConfig>

export const useSetupConfig = ({
  configEditorOverride,
  region,
}: {
  configEditorOverride?: ConfigEditorOverride
  /**
   * Region resolved outside of the hook (e.g. from the request host during
   * SSR). Without it `envConfig.region` is used, which is always `CZ` on the
   * server because there is no `window` to derive the host from.
   */
  region?: GLOBALS.Region
}) => {
  const router = useRouter()
  const [configVersion] = useAtom(configVersionAtom)
  const [remoteLanguage] = useAtom(remoteLanguageAtom)

  const domainConfig =
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    !isNil(router?.locale)
      ? {
          language: (remoteLanguage?.userLanguage.language ??
            remoteLanguage?.userLanguage.default ??
            router.locale.split('-')[0]) as GLOBALS.Language,
        }
      : undefined

  const config = useMemo(
    () => setupAppConfig({ ...domainConfig, region }, configEditorOverride),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [configVersion, remoteLanguage, region]
  )

  return config
}
